import type { UserInvestmentProfile } from '@fruitful/investment-management';
import { formatInvestmentProfileValue } from '@fruitful/investment-management';
import { useState } from 'react';
import { BodyMediumRegular, SelectSingle, Spinner, YStack } from 'smoothie';

import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';

interface InvestmentProfileQuestionStepProps<T extends string> {
    question: string;
    options: Record<string, T>;
    initialValue?: T;
    field: keyof UserInvestmentProfile;
    onSelect: (updates: Partial<UserInvestmentProfile>) => Promise<void>;
    isPending?: boolean;
}

export const InvestmentProfileQuestionStep = <T extends string>({
    question,
    options,
    initialValue,
    field,
    onSelect,
    isPending,
}: InvestmentProfileQuestionStepProps<T>) => {
    useTrackViewOnFocus('Profile/InvestmentProfileQuestionStep');
    const optionValues = Object.values(options);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(
        initialValue != null
            ? optionValues.findIndex((v) => v === initialValue)
            : null,
    );

    return (
        <>
            <YStack gap={16} paddingVertical={16}>
                <BodyMediumRegular>{question}</BodyMediumRegular>
                <SelectSingle
                    options={optionValues.map((value) => ({
                        title: formatInvestmentProfileValue(field, value),
                    }))}
                    selectedIndex={selectedIndex}
                    onSelect={(index) => {
                        setSelectedIndex(index);
                        void onSelect({ [field]: optionValues[index] });
                    }}
                />
            </YStack>
            {isPending && (
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                    height="100%"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    backgroundColor="rgba(255, 255, 255, 0.6)"
                >
                    <Spinner />
                </YStack>
            )}
        </>
    );
};
